import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import TaskSummaryHeader from './TaskSummaryHeader'
import Icon from 'components/Icon/Icon'
import { CAMPAIGN_STATUS_ENUM, selectEditingCampaign, selectEditingCampaignType } from 'state/campaigns'
import { selectTasks, TaskState, updateTaskOrder } from 'state/tasks'
import { ViewTypeContext } from 'components/Campaign'
import { EditingState, TaskSections } from '../'
import Divider from 'components/Shared/Divider'
import { TaskSidebarContainer } from '../styles'
import { TASK_TYPE_ENUM } from 'services/api/constants'
import { SubListButton, StepListButton, SubListDropListContainer } from '../styles'
import TaskTypeSelector from '../TaskTypeSelector'
import { noop } from 'lodash'
import { selectorEditorErrors } from 'state/error'
import ErrorDotTitle from 'components/Shared/ErrorDotTitle'
import Registration from './Registration'
import { setConfirmPopup } from 'state/confirmPopup'
import { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import EditCampaignRestricted from 'components/Shared/Permission/EditCampaignRestricted'
import { useCheckEditCampaignPermission, useShowPrivateCampaignBudget } from 'hooks/permissions'
import { useNewTranslation } from 'hooks'
import { TabTypes, useIdentifiersMap, useSetIdentifierRoute, useCommentCount } from 'hooks/comment'
import { selectIsEngagementStepTypeHelper } from 'state/constants'
import RectangleButton from 'particles/Button/RectangleButton'
import {
    DragDropContext,
    Droppable,
    Draggable,
    DraggableProvided,
    DropResult,
    DroppableProvided,
} from 'react-beautiful-dnd'
import { reorderList } from 'components/utils'
import Text from 'particles/Text'
import { CREATE_TASKS_ERRORS } from 'state/error'
import qs from 'query-string'
import { selectIsInternalUser } from 'state/profile'

interface TaskSidebarProps {
    className?: string
    currentState: EditingState
    selectRegistrationPhase: () => void
    selectTask: (task: TaskState, editingState: EditingState) => void
    id?: string
    createTask: (taskType: TASK_TYPE_ENUM) => void
    removeTask: (uuid: string) => void
    onCloseSideBar?: () => void
    isSmallSideBar?: boolean
    selectedTask: TaskState | null
    setSelectedTaskSection: (section: TaskSections) => void
    selectedTaskSection: TaskSections
}

const TaskSidebar: React.FC<TaskSidebarProps> = ({
    className,
    selectTask,
    createTask,
    removeTask,
    currentState,
    selectRegistrationPhase,
    onCloseSideBar = noop,
    isSmallSideBar = false,
    selectedTask,
    setSelectedTaskSection,
    id,
    selectedTaskSection,
}) => {
    const [t] = useNewTranslation()
    const [selectedStepUuid, setSelectedStepUuid] = useState<string | null>(null)
    const [anchorTaskListMenuEl, setAnchorTaskListMenuEl] = useState<null | HTMLElement>(null)
    const location = useLocation()
    const params = qs.parse(location.search)
    const { selectedIdentifier, stepUuid } = params
    const viewType = useContext(ViewTypeContext)
    const dispatch = useDispatch()
    const isInternalUser = useSelector(selectIsInternalUser)
    const isSelfServe = useSelector(selectEditingCampaignType)
    const isEngagementStepType = useSelector(selectIsEngagementStepTypeHelper)
    const editingCampaign = useSelector(selectEditingCampaign)
    const tasks = useSelector(selectTasks)
    const errors = useSelector(selectorEditorErrors([CREATE_TASKS_ERRORS.TASKS, CREATE_TASKS_ERRORS.TASK_ORDERING]))
    const campaignStatus = editingCampaign ? editingCampaign.status : undefined
    const campaignUuid = editingCampaign ? editingCampaign.uuid : null
    const hasCreateOrEditBriefPermission = Boolean(useCheckEditCampaignPermission(campaignStatus))
    const hasShowPrivateCampaignBudget = useShowPrivateCampaignBudget(Boolean(editingCampaign?.is_private))
    const { tasksIdentifierMap } = useIdentifiersMap()
    const commentCount = useCommentCount()
    const [updateRoute] = useSetIdentifierRoute()

    const getIcons = (taskType: string): React.ReactElement => {
        switch (taskType) {
            case TASK_TYPE_ENUM.PURCHASE_PRODUCT: {
                return <Icon.PurchaseCartIcon className="stroke-can-change" />
            }
            case TASK_TYPE_ENUM.ATTEND_EVENT: {
                return <Icon.AttendEventIcon className="stroke-can-change" />
            }
            case TASK_TYPE_ENUM.IG_POST:
            case TASK_TYPE_ENUM.IG_POST_V02:
            case TASK_TYPE_ENUM.IG_STORY:
            case TASK_TYPE_ENUM.IG_STORY_V02:
            case TASK_TYPE_ENUM.IG_REEL: {
                return <Icon.InstagramIcon className="stroke-can-change" />
            }
            case TASK_TYPE_ENUM.FB_POST:
            case TASK_TYPE_ENUM.FB_POST_V02: {
                return <Icon.FacebookIcon className="stroke-can-change" />
            }
            case TASK_TYPE_ENUM.TIKTOK_POST: {
                return <Icon.TiktokIcon2 className="stroke-can-change" />
            }
            case TASK_TYPE_ENUM.CUSTOM: {
                return <Icon.CustomTaskIcon className="fill-can-change" />
            }
            default: {
                return <Icon.AttendEventIcon className="stroke-can-change" />
            }
        }
    }

    const handleSetTaskSection = (
        e: React.MouseEvent<HTMLDivElement>,
        section: TaskSections,
        stepUuid: string | null = null,
    ): void => {
        e.preventDefault()
        e.stopPropagation()
        setSelectedTaskSection(section)
        setSelectedStepUuid(stepUuid)
    }

    const redirectIdentifierTab = (tab: string) => {
        switch (tab) {
            case TabTypes.SETUP: {
                if (selectedTaskSection !== TaskSections.SETUP) {
                    setSelectedTaskSection(TaskSections.SETUP)
                }
                break
            }
            case TabTypes.STEPS: {
                if (selectedTaskSection !== TaskSections.STEPS) {
                    setSelectedTaskSection(TaskSections.STEPS)
                }
                break
            }
            case TabTypes.SCHEDULE: {
                if (selectedTaskSection !== TaskSections.SCHEDULE) {
                    setSelectedTaskSection(TaskSections.SCHEDULE)
                }
                break
            }
            case TabTypes.PAYOUT: {
                if (selectedTaskSection !== TaskSections.PAYOUT) {
                    setSelectedTaskSection(TaskSections.PAYOUT)
                }
                break
            }
        }
    }

    const isCampaignStatusDraggable = useMemo(() => {
        return Boolean(
            campaignStatus !== undefined &&
                ![CAMPAIGN_STATUS_ENUM.CLOSED, CAMPAIGN_STATUS_ENUM.LIVE].includes(campaignStatus),
        )
    }, [campaignStatus])

    const isDraggable = useMemo(() => {
        const isUserDraggable = isInternalUser ? !isSelfServe : isSelfServe
        return Boolean(
            hasCreateOrEditBriefPermission && tasks && tasks.length > 1 && isCampaignStatusDraggable && isUserDraggable,
        )
    }, [hasCreateOrEditBriefPermission, tasks, isCampaignStatusDraggable, isInternalUser, isSelfServe])

    const onDragEnd = useCallback(
        (tasks: TaskState[], result: DropResult): void => {
            if (result.destination && campaignUuid && hasCreateOrEditBriefPermission) {
                const current = result.source.index
                const destination = result.destination.index
                const previousOrder = tasks.map((task: TaskState) => task.uuid)
                const newOrders = reorderList([...previousOrder], current, destination)
                dispatch(updateTaskOrder({ campaignUuid: campaignUuid, ordering: newOrders }))
            }
        },
        [campaignUuid, hasCreateOrEditBriefPermission],
    )

    useEffect(() => {
        if (!selectedStepUuid) return
        const selectDom = window.document.getElementById(selectedStepUuid)
        if (!selectDom) {
            return
        } else {
            selectDom.scrollIntoView({ behavior: 'smooth' })
        }
    }, [selectedStepUuid])

    useEffect(() => {
        let timeoutId: any

        const isCampaignEditorValueChange = localStorage.getItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
        if (typeof selectedIdentifier === 'string' && tasksIdentifierMap[selectedIdentifier]) {
            const { taskId, tab } = tasksIdentifierMap[selectedIdentifier]
            if (taskId === 'registration') {
                if (currentState === EditingState.REGISTRATION_PHASE) {
                    redirectIdentifierTab(tab)
                } else {
                    if (isCampaignEditorValueChange) {
                        dispatch(
                            setConfirmPopup({
                                show: true,
                                onConfirm: () => {
                                    selectRegistrationPhase()
                                    redirectIdentifierTab(tab)
                                    updateRoute({
                                        identifier: selectedIdentifier,
                                    })
                                },
                            }),
                        )
                    } else {
                        selectRegistrationPhase()
                        redirectIdentifierTab(tab)
                        updateRoute({ identifier: selectedIdentifier })
                    }
                }
            } else {
                if (selectedTask?.uuid === taskId) {
                    redirectIdentifierTab(tab)
                } else {
                    const task = tasks.find(task => task.uuid === taskId)
                    if (task) {
                        if (isCampaignEditorValueChange) {
                            dispatch(
                                setConfirmPopup({
                                    show: true,
                                    onConfirm: () => {
                                        selectTask(task, EditingState.EDIT_STEPS)
                                        redirectIdentifierTab(tab)
                                        updateRoute({ identifier: selectedIdentifier })
                                    },
                                }),
                            )
                        } else {
                            selectTask(task, EditingState.EDIT_STEPS)
                            timeoutId = setTimeout(() => {
                                redirectIdentifierTab(tab)
                            }, 1000)
                        }
                    }
                }
            }
        } else if (typeof stepUuid === 'string') {
            timeoutId = setTimeout(() => {
                setSelectedStepUuid(stepUuid)
            }, 500)
        }
        return () => {
            clearTimeout(timeoutId)
        }
    }, [currentState, tasksIdentifierMap, selectedIdentifier, tasks, stepUuid])

    return (
        <>
            <TaskSidebarContainer className={className}>
                <div className="title">
                    <div>{t('campaign.section.tasks')}</div>
                    {[CAMPAIGN_STATUS_ENUM.DRAFT, CAMPAIGN_STATUS_ENUM.FOR_EDIT, undefined].includes(campaignStatus) &&
                        viewType === 'edit' && (
                            <EditCampaignRestricted campaignStatus={campaignStatus}>
                                <RectangleButton
                                    form="outlined"
                                    size="s"
                                    icon="plus"
                                    data-testid="test-add-task"
                                    label={t('button.addTask')}
                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                        setAnchorTaskListMenuEl(event.currentTarget)
                                    }}
                                    id={id}
                                />
                            </EditCampaignRestricted>
                        )}
                </div>
                {errors && errors[CREATE_TASKS_ERRORS.TASK_ORDERING] && (
                    <Text scale="caption400" color="error" className="ordering-error">
                        {t('errorMessage.validation.wrongTaskOrder')}
                    </Text>
                )}

                <Divider />
                {editingCampaign && editingCampaign.registration_enable ? (
                    <Registration
                        currentState={currentState}
                        onCloseSideBar={onCloseSideBar}
                        isSmallSideBar={isSmallSideBar}
                        selectedTaskSection={selectedTaskSection}
                        setSelectedTaskSection={setSelectedTaskSection}
                        selectRegistrationPhase={selectRegistrationPhase}
                        handleSetTaskSection={handleSetTaskSection}
                    />
                ) : null}
                <DragDropContext onDragEnd={(result: DropResult) => onDragEnd(tasks, result)}>
                    <Droppable droppableId="droppable">
                        {(provided: DroppableProvided): JSX.Element => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                {tasks.map((task: TaskState, index: number) => (
                                    <Draggable
                                        key={`${task?.uuid}`}
                                        draggableId={`${task?.uuid}`}
                                        index={index}
                                        isDragDisabled={!isDraggable}
                                    >
                                        {(provided: DraggableProvided, snapshot): JSX.Element => {
                                            const taskErrors =
                                                errors &&
                                                errors[CREATE_TASKS_ERRORS.TASKS] &&
                                                errors[CREATE_TASKS_ERRORS.TASKS][task?.uuid]
                                            const isTaskHasErrors = Boolean(taskErrors)
                                            const isSetUpHasErrors = Boolean(
                                                taskErrors &&
                                                    (taskErrors['title'] ||
                                                        // only custom task
                                                        taskErrors['platform_content_type'] ||
                                                        taskErrors['platform_content_type_others']),
                                            )

                                            let isStepsHasErrors = false
                                            if (isTaskHasErrors && taskErrors && taskErrors['steps']) {
                                                for (const key in taskErrors['steps']) {
                                                    Object.keys(taskErrors['steps'][key]).forEach((key: string) => {
                                                        if (key !== 'time_window') {
                                                            isStepsHasErrors = true
                                                        }
                                                    })
                                                }
                                            }
                                            const isPayoutHasErrors = Boolean(
                                                isTaskHasErrors && taskErrors && taskErrors['payouts'],
                                            )
                                            let isTaskScheduleHasErrors = false
                                            if (taskErrors && taskErrors['steps']) {
                                                const stepsData = Object.values(taskErrors['steps'])
                                                if (Array.isArray(stepsData)) {
                                                    stepsData.forEach((data: any) => {
                                                        if (Object.keys(data).includes('time_window')) {
                                                            isTaskScheduleHasErrors = true
                                                        }
                                                    })
                                                }
                                            }
                                            const start = task?.steps[0]?.time_window?.start
                                            const end =
                                                task.type ===
                                                    (TASK_TYPE_ENUM.ATTEND_EVENT || TASK_TYPE_ENUM.PURCHASE_PRODUCT) &&
                                                !task?.steps[task.steps.length - 1]?.time_window
                                                    ? task?.steps[0]?.time_window?.end
                                                    : task?.steps[task.steps.length - 1]?.time_window?.end
                                            const taskSchedule = {
                                                start,
                                                end,
                                            }

                                            return (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    className={`task-card ${snapshot.isDragging ? 'isDragging' : ''}`}
                                                >
                                                    <div {...provided.dragHandleProps}>
                                                        <TaskSummaryHeader
                                                            className="task-button"
                                                            schedule={taskSchedule}
                                                            title={task.title}
                                                            isActive={
                                                                selectedTask ? selectedTask.uuid === task.uuid : false
                                                            }
                                                            showClose={
                                                                campaignStatus !== CAMPAIGN_STATUS_ENUM.LIVE &&
                                                                campaignStatus !== CAMPAIGN_STATUS_ENUM.FOR_REVIEW &&
                                                                hasCreateOrEditBriefPermission &&
                                                                (isInternalUser
                                                                    ? !editingCampaign?.self_serve
                                                                    : editingCampaign?.self_serve)
                                                            }
                                                            onActive={(): void => {
                                                                const isCampaignEditorValueChange =
                                                                    localStorage.getItem(
                                                                        RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE,
                                                                    )
                                                                if (isSmallSideBar && onCloseSideBar) {
                                                                    onCloseSideBar()
                                                                }
                                                                if (isCampaignEditorValueChange) {
                                                                    dispatch(
                                                                        setConfirmPopup({
                                                                            show: true,
                                                                            onConfirm: () => {
                                                                                setSelectedTaskSection(
                                                                                    TaskSections.SETUP,
                                                                                )
                                                                                selectTask(
                                                                                    task,
                                                                                    EditingState.EDIT_STEPS,
                                                                                )
                                                                                updateRoute({
                                                                                    taskUuid: task?.uuid,
                                                                                    section: TaskSections.SETUP,
                                                                                    stepUuid: null,
                                                                                })
                                                                            },
                                                                        }),
                                                                    )
                                                                } else {
                                                                    updateRoute({
                                                                        taskUuid: task?.uuid,
                                                                        section: TaskSections.SETUP,
                                                                        stepUuid: null,
                                                                    })
                                                                    setSelectedTaskSection(TaskSections.SETUP)
                                                                    selectTask(task, EditingState.EDIT_STEPS)
                                                                }
                                                            }}
                                                            onClose={(): void => {
                                                                localStorage.removeItem(
                                                                    RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE,
                                                                )
                                                                removeTask(task.uuid)
                                                            }}
                                                            isShowErrorDot={isTaskHasErrors}
                                                            isDraggable={isDraggable}
                                                            isDragDisabled={tasks.length === 1}
                                                            icon={getIcons(task.type)}
                                                            commentCount={commentCount(task.uuid)}
                                                        />
                                                    </div>

                                                    {selectedTask && selectedTask.uuid === task.uuid && (
                                                        <div
                                                            className={`steps-buttons-group ${
                                                                campaignStatus === CAMPAIGN_STATUS_ENUM.LIVE
                                                                    ? 'non-draggable-group'
                                                                    : ''
                                                            }`}
                                                        >
                                                            <SubListButton
                                                                isActive={selectedTaskSection === TaskSections.SETUP}
                                                                onClick={(
                                                                    e: React.MouseEvent<HTMLDivElement>,
                                                                ): void => {
                                                                    updateRoute({
                                                                        taskUuid: task?.uuid,
                                                                        section: TaskSections.SETUP,
                                                                    })
                                                                    handleSetTaskSection(e, TaskSections.SETUP)
                                                                }}
                                                                data-testid="test-campaign-task-setup"
                                                            >
                                                                <ErrorDotTitle
                                                                    title={t('campaign.section.taskSetup')}
                                                                    isShowDot={isSetUpHasErrors}
                                                                    commentCount={commentCount(
                                                                        task.uuid,
                                                                        TaskSections.SETUP,
                                                                    )}
                                                                />
                                                            </SubListButton>
                                                            <div>
                                                                <SubListButton
                                                                    onClick={(
                                                                        e: React.MouseEvent<HTMLDivElement>,
                                                                    ): void => {
                                                                        updateRoute({
                                                                            taskUuid: task?.uuid,
                                                                            section: TaskSections.STEPS,
                                                                        })
                                                                        handleSetTaskSection(e, TaskSections.STEPS)
                                                                    }}
                                                                    isActive={
                                                                        selectedTaskSection === TaskSections.STEPS
                                                                    }
                                                                    data-testid="test-campaign-task-step-editor"
                                                                >
                                                                    <ErrorDotTitle
                                                                        title={t('campaign.section.steps')}
                                                                        isShowDot={isStepsHasErrors}
                                                                        commentCount={commentCount(
                                                                            task.uuid,
                                                                            TaskSections.STEPS,
                                                                        )}
                                                                    />
                                                                </SubListButton>
                                                                {selectedTask.steps.length > 0 && (
                                                                    <SubListDropListContainer
                                                                        isActive={
                                                                            selectedTaskSection === TaskSections.STEPS
                                                                        }
                                                                    >
                                                                        <div className="drop-list">
                                                                            {selectedTask.steps.map((step, index) => {
                                                                                let isSubStepHasErrors = false
                                                                                if (
                                                                                    taskErrors &&
                                                                                    taskErrors['steps'] &&
                                                                                    taskErrors['steps'][step.uuid]
                                                                                ) {
                                                                                    Object.keys(
                                                                                        taskErrors['steps'][step.uuid],
                                                                                    ).forEach((key: string) => {
                                                                                        if (key !== 'time_window') {
                                                                                            isSubStepHasErrors = true
                                                                                        }
                                                                                    })
                                                                                }

                                                                                return (
                                                                                    <StepListButton
                                                                                        key={step.uuid}
                                                                                        onClick={(
                                                                                            e: React.MouseEvent<HTMLDivElement>,
                                                                                        ): void => {
                                                                                            updateRoute({
                                                                                                taskUuid: task?.uuid,
                                                                                                section:
                                                                                                    TaskSections.STEPS,
                                                                                                stepUuid: step.uuid,
                                                                                            })
                                                                                            handleSetTaskSection(
                                                                                                e,
                                                                                                TaskSections.STEPS,
                                                                                                step.uuid,
                                                                                            )
                                                                                        }}
                                                                                        isActive={
                                                                                            selectedStepUuid ===
                                                                                            step.uuid
                                                                                        }
                                                                                    >
                                                                                        <ErrorDotTitle
                                                                                            title={
                                                                                                selectedTask.type ===
                                                                                                    TASK_TYPE_ENUM.CUSTOM &&
                                                                                                isEngagementStepType(
                                                                                                    step.type,
                                                                                                )
                                                                                                    ? t(
                                                                                                          'sidebar.campaignEditor.proofAndEngagement',
                                                                                                      )
                                                                                                    : `${t(
                                                                                                          'campaign.section.steps',
                                                                                                      )} ${index + 1}`
                                                                                            }
                                                                                            isShowDot={
                                                                                                isSubStepHasErrors
                                                                                            }
                                                                                        />
                                                                                    </StepListButton>
                                                                                )
                                                                            })}
                                                                        </div>
                                                                    </SubListDropListContainer>
                                                                )}
                                                            </div>
                                                            <SubListButton
                                                                isActive={selectedTaskSection === TaskSections.SCHEDULE}
                                                                onClick={(
                                                                    e: React.MouseEvent<HTMLDivElement>,
                                                                ): void => {
                                                                    if (selectedTaskSection !== TaskSections.SCHEDULE) {
                                                                        updateRoute({
                                                                            taskUuid: task?.uuid,
                                                                            section: TaskSections.SCHEDULE,
                                                                            stepUuid: null,
                                                                        })
                                                                    }
                                                                    handleSetTaskSection(e, TaskSections.SCHEDULE)
                                                                }}
                                                                data-testid="test-campaign-task-schedule"
                                                            >
                                                                <ErrorDotTitle
                                                                    title={t(
                                                                        'campaignEditor.createTask.taskSchedulePageTitle',
                                                                    )}
                                                                    isShowDot={isTaskScheduleHasErrors}
                                                                    commentCount={commentCount(
                                                                        task.uuid,
                                                                        TaskSections.SCHEDULE,
                                                                    )}
                                                                />
                                                            </SubListButton>
                                                            {hasShowPrivateCampaignBudget ? (
                                                                <SubListButton
                                                                    isActive={
                                                                        selectedTaskSection === TaskSections.PAYOUT
                                                                    }
                                                                    onClick={(
                                                                        e: React.MouseEvent<HTMLDivElement>,
                                                                    ): void => {
                                                                        if (
                                                                            selectedTaskSection !== TaskSections.PAYOUT
                                                                        ) {
                                                                            updateRoute({
                                                                                taskUuid: task?.uuid,
                                                                                section: TaskSections.PAYOUT,
                                                                                stepUuid: null,
                                                                            })
                                                                        }
                                                                        handleSetTaskSection(e, TaskSections.PAYOUT)
                                                                    }}
                                                                    data-testid="test-campaign-task-payout"
                                                                >
                                                                    <ErrorDotTitle
                                                                        title={t(
                                                                            'campaignEditor.createTask.payoutPageTitle',
                                                                        )}
                                                                        isShowDot={isPayoutHasErrors}
                                                                        commentCount={commentCount(
                                                                            task.uuid,
                                                                            TaskSections.PAYOUT,
                                                                        )}
                                                                    />
                                                                </SubListButton>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                    {!snapshot.isDragging && <Divider />}
                                                </div>
                                            )
                                        }}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </TaskSidebarContainer>
            <TaskTypeSelector
                campaignStatus={campaignStatus}
                createTask={createTask}
                onHideSelector={(): void => {
                    setAnchorTaskListMenuEl(null)
                }}
                anchorEl={anchorTaskListMenuEl}
            />
        </>
    )
}

export default TaskSidebar
